/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import Select from 'react-select/dist/declarations/src/Select';
import { createPortal } from 'react-dom';
import ReactSelect, { SingleValue, MultiValue, GroupBase, components } from 'react-select';
import { matchSorter } from 'match-sorter';
import Model from '@tripian/model';
import Button from '../base/Button/Button';
import BUTTON_TYPES from '../base/Button/ButtonTypes';
import CitySearchModal from '../CitySearchModal/CitySearchModal';
// eslint-disable-next-line import/no-cycle
import { SvgIcons } from '../../index';
import classes from './DestinationSelect.scss';

export type RSelectOption = {
  id: number;
  label: string;
  payload: { destinationId: number; destinationName: string; coordinate: Model.Coordinate; parentName: string };
  isSelected?: boolean;
};

export type SuggestedCityMatch = {
  city: {
    id: string;
    name: string;
    description: string;
  };
  reason: string;
  match_score: number;
  highlights: string[];
};

interface IDestinationSelect {
  options: RSelectOption[];
  selectedOptionId?: number;
  onSelectedOptionChange: (selectedOption: RSelectOption) => void;
  placeHolder?: string;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  showAiSuggestions?: boolean;
  suggestions?: SuggestedCityMatch[];
  onSuggestionsSearch?: (text: string, isQuickSearch?: boolean) => void;
  isSearching?: boolean;
  onSelectSuggestedCity?: (cityId: number) => void;
  onClearSuggestions?: () => void;
  t: (value: Model.TranslationKey) => string;
  showSearchIcon?: boolean;
}

const DestinationSelect: React.FC<IDestinationSelect> = ({
  options,
  selectedOptionId,
  onSelectedOptionChange,
  placeHolder = 'Please select',
  disabled = false,
  onFocus,
  onBlur,
  showAiSuggestions = false,
  suggestions = [],
  onSuggestionsSearch,
  isSearching = false,
  onSelectSuggestedCity,
  onClearSuggestions,
  t,
  showSearchIcon = false,
}) => {
  const selectedOption = useMemo(() => options.find((option) => option.id === selectedOptionId), [selectedOptionId, options]);
  const [destinationSearchInput, setDestinationSearchInput] = useState<string>('');
  const [isSuggestionsModalOpen, setSuggestionsModalOpen] = useState(false);
  const selectRef = useRef<Select<RSelectOption, false, GroupBase<RSelectOption>>>(null);

  // Store the current search input separately so it's not lost when ReactSelect resets it
  const [lastSearchText, setLastSearchText] = useState<string>('');

  // Track whether we've already searched for this term to avoid duplicate searches
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string>('');

  const handleChange = (newSelectedOption: MultiValue<RSelectOption> | SingleValue<RSelectOption>) => {
    onSelectedOptionChange(newSelectedOption as RSelectOption);
  };

  const noOptionsMessage = (obj: { inputValue: string }) => {
    if (obj.inputValue.length > 2) {
      return <span>{t('destination.search.noResults')}</span>;
    }
    return null;
  };

  const SafeText: React.FC<{ text: string }> = ({ text }) => (
    <span translate="no" data-nosnippet contentEditable={false} role="presentation" suppressContentEditableWarning>
      {text.split(' ').map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} translate="no">
          {part}{' '}
        </span>
      ))}
    </span>
  );

  const formatOptionLabel = (option: RSelectOption) => (
    <div className={classes.optionLabel} translate="no" data-nosnippet>
      <div className="h-6 w-6">
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </div>
      <div className={classes.optionLabelText}>
        <div>
          <SafeText text={option.payload.destinationName} />
        </div>
        <div>
          <SafeText text={option.payload.parentName} />
        </div>
      </div>
    </div>
  );

  // Update lastSearchText whenever destinationSearchInput changes
  useEffect(() => {
    if (destinationSearchInput.trim() !== '') {
      setLastSearchText(destinationSearchInput);
    }
  }, [destinationSearchInput]);

  // Reset lastSearchedTerm when modal closes
  useEffect(() => {
    if (!isSuggestionsModalOpen) {
      // Reset the last searched term when modal closes
      // so next time it opens, we don't consider any term as "already searched"
      setLastSearchedTerm('');
    }
  }, [isSuggestionsModalOpen]);

  // Handle search for AI suggestions
  const handleSearchChange = (text: string, isQuickSearch = false) => {
    if (onSuggestionsSearch) {
      // Only perform the search if this is a new term we haven't searched for yet
      // or if the user explicitly presses Enter (isQuickSearch = false)
      if (text !== lastSearchedTerm || !isQuickSearch) {
        onSuggestionsSearch(text, isQuickSearch);
        setLastSearchedTerm(text);
      }
    }
  };

  // Handle selection of suggested city
  const handleSuggestedCitySelect = (cityId: number) => {
    const cityIdNumber = parseInt(String(cityId), 10);

    // Call the parent handler if provided
    if (onSelectSuggestedCity) {
      onSelectSuggestedCity(cityIdNumber);
    }

    // Try matching by destinationId
    let matchingOption = options.find((option) => option.payload.destinationId === cityIdNumber);

    // If no match, try matching by the option id
    if (!matchingOption) {
      matchingOption = options.find((option) => option.id === cityIdNumber);
    }

    // If we found a matching option, select it
    if (matchingOption) {
      onSelectedOptionChange(matchingOption);
    } else {
      // Try string comparison as a last resort
      matchingOption = options.find((option) => String(option.payload.destinationId) === String(cityId) || String(option.id) === String(cityId));

      if (matchingOption) {
        onSelectedOptionChange(matchingOption);
      } else {
        // eslint-disable-next-line no-console
        console.warn('No matching option found for cityId:', cityId, 'Type:', typeof cityId);
      }
    }

    // Close the modal
    setSuggestionsModalOpen(false);
  };

  const filteredOptions = useMemo(() => matchSorter(options, destinationSearchInput, { keys: ['label'] }), [options, destinationSearchInput]);

  // Open modal and automatically search with current input
  const handleOpenSuggestionsModal = () => {
    setSuggestionsModalOpen(true);

    // We don't auto-search when opening the modal anymore
    // User needs to press Enter to initiate a search
  };
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <>
      <div
        className="relative"
        translate="no"
        data-nosnippet
        style={{
          ...(showSearchIcon ? { width: '100%' } : {}),
        }}
      >
        <ReactSelect
          ref={selectRef}
          options={filteredOptions}
          value={selectedOption}
          onChange={handleChange}
          onInputChange={(value, meta) => {
            if (meta.action === 'input-change') {
              setDestinationSearchInput(value);
            }
          }}
          placeholder={placeHolder}
          className="destinationSelect"
          classNamePrefix="destinationSelect"
          isDisabled={disabled}
          isOptionDisabled={(option) => option.id === selectedOptionId}
          onFocus={() => onFocus && onFocus()}
          onBlur={() => onBlur && onBlur()}
          noOptionsMessage={noOptionsMessage}
          formatOptionLabel={formatOptionLabel}
          menuPlacement="auto"
          menuPortalTarget={isMobile ? undefined : document.body}
          styles={isMobile ? undefined : { menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </div>

      {/* AI City Suggestions Modal */}
      {showAiSuggestions &&
        isSuggestionsModalOpen &&
        createPortal(
          <CitySearchModal
            isOpen={isSuggestionsModalOpen}
            onClose={() => setSuggestionsModalOpen(false)}
            suggestions={suggestions || []}
            onSelectCity={handleSuggestedCitySelect}
            searchText={lastSearchText}
            handleSearchChange={handleSearchChange}
            isSearching={isSearching}
            onClearSuggestions={onClearSuggestions}
            t={t}
          />,
          document.body,
        )}
    </>
  );
};

export default DestinationSelect;
